import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import {
  formatArticleDate,
  getVisitCounts,
  listPublishedArticles,
  listPublishedArticlesForCommerce,
  readingMinutes,
  type BlogArticle,
} from '@/lib/blog';

const FALLBACK_COVER =
  'https://readdy.ai/api/search-image?query=Soft%20editorial%20still%20life%20of%20a%20desk%20with%20paper%20documents%20and%20warm%20beige%20tones%2C%20minimal%20modern%20photography%2C%20subtle%20shadows&width=800&height=500&seq=merchant-blog-cover-2026&orientation=landscape';

/**
 * ZifekBlogSection — affiche les derniers articles du blog Zifek
 * directement sur le site public du marchand (conseils business,
 * gestion, vente…), avec le compteur de lectures réel.
 */
export default function ZifekBlogSection({ limit = 3 }: { limit?: number }) {
  const { tenant, isTenant } = useTenant();
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [views, setViews] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Sur la boutique d'un marchand, on affiche SES articles ;
        // s'il n'en a pas encore, on retombe sur les conseils Zifek.
        let rows: BlogArticle[] = [];
        if (isTenant && tenant?.id) {
          rows = await listPublishedArticlesForCommerce(tenant.id, limit);
        }
        if (rows.length === 0) {
          rows = await listPublishedArticles(limit);
        }
        if (!alive) return;
        setArticles(rows);
        if (rows.length > 0) {
          const counts = await getVisitCounts();
          if (alive) setViews(counts.byArticle);
        }
      } catch {
        if (alive) setArticles([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [limit, isTenant, tenant?.id]);

  if (loading || articles.length === 0) return null;

  return (
    <section className="py-10 md:py-16 bg-background-100/50">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 text-[11px] font-semibold mb-2">
              Blog
            </span>
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center">
              <i className="ri-quill-pen-line mr-2 text-primary-500"></i>
              {isTenant ? 'Nos derniers articles' : 'Conseils pour votre activité'}
            </h2>
            <p className="text-sm text-foreground-500 mt-1">
              {isTenant ? 'Les nouveautés et conseils de la maison.' : 'Nos guides pratiques pour vendre, gérer et développer votre commerce.'}
            </p>
          </div>
          <Link
            to="/blog"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer no-underline whitespace-nowrap"
          >
            Tous les articles <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {articles.map((a) => (
            <Link
              key={a.id}
              to={`/blog/${a.slug || a.id}`}
              className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden no-underline hover:border-primary-200 transition-colors flex flex-col"
            >
              <div className="w-full h-40 overflow-hidden bg-background-100">
                <img
                  src={a.cover_image || FALLBACK_COVER}
                  alt={a.title}
                  title={`${a.title} — blog Zifek`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-4 flex flex-col flex-1">
                <h3 className="text-sm font-semibold text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors line-clamp-2">
                  {a.title}
                </h3>
                {a.excerpt && (
                  <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed line-clamp-2">{a.excerpt}</p>
                )}
                <div className="mt-auto pt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {formatArticleDate(a.published_at || a.created_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {readingMinutes(a.content)} min
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line text-accent-600"></i>
                    {(views[a.id] || 0).toLocaleString('fr-FR')}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}